// Pure readers for an answer's `pausedRules` (KPR-1372). No network import, so
// a component that only displays them needs no loading state; the packages
// service re-exports them.

/** How many of the person's paused rules a notice names before it counts the rest. */
export const PAUSED_RULES_SHOWN = 5;

/**
 * The person's OWN running rules an install, an add-on or a bundle update
 * paused (the answer's `pausedRules`, keepr-api KPR-1323 and KPR-1354),
 * read for a notice (KPR-1372). They are not the pack's own rules, which
 * arrive paused and are counted elsewhere: a rule the pack also brought
 * (`ownIds`, the ids of the answer's `automations` and `notifications`) is
 * said once, as the pack's, so it is left out here. Each rule once, in the
 * order sent. A name is required; a `collection_id` is what the link needs,
 * and an entry without one is named without a link. Anything that is not a
 * list says nothing.
 * Returns `{ total, shown, more }`; `shown` rows are `{ key, name, href }`
 * (`href` null without a collection), the first PAUSED_RULES_SHOWN.
 */
export function pausedRulesOf(pausedRules, ownIds = []) {
	const own = new Set((Array.isArray(ownIds) ? ownIds : []).map(String));
	const seen = new Set();
	const rows = [];
	(Array.isArray(pausedRules) ? pausedRules : []).forEach((rule, i) => {
		if (!rule || typeof rule !== 'object') return;
		const name = typeof rule.name === 'string' ? rule.name.trim() : '';
		if (!name) return;
		const id = rule.id == null ? '' : String(rule.id);
		if (id && (own.has(id) || seen.has(id))) return;
		if (id) seen.add(id);
		const cid = typeof rule.collection_id === 'string' ? rule.collection_id.trim() : '';
		rows.push({ key: id || `${i}:${name}`, name, href: cid ? `/collections/${encodeURIComponent(cid)}/form?tab=automations` : null });
	});
	return { total: rows.length, shown: rows.slice(0, PAUSED_RULES_SHOWN), more: Math.max(0, rows.length - PAUSED_RULES_SHOWN) };
}

/** The ids of the rules and notifications the pack itself brought (arriving paused). */
export function packOwnRuleIds(answer) {
	return [ ...(Array.isArray(answer?.automations) ? answer.automations : []), ...(Array.isArray(answer?.notifications) ? answer.notifications : []) ]
		.map(r => r?.id).filter(id => id != null);
}

/** The heading and the reason for the person's own paused rules; '' lines when none. */
export function pausedYourRulesWords(total) {
	if (!total) return { heading: '', reason: '' };
	return total === 1
		? { heading: 'Paused your rule', reason: 'It now acts on a value that changes with the date, so it waits for you to turn it back on.' }
		: { heading: `Paused ${total} of your rules`, reason: 'They now act on a value that changes with the date, so they wait for you to turn them back on.' };
}
